import { api } from "@/lib/api";

const b64uToBuf = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u.buffer;
};
const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const passkeySupported = () => !!(window.PublicKeyCredential && navigator.credentials);

// iOS-safe passkey registration is split into 3 steps so that
// navigator.credentials.create() is invoked SYNCHRONOUSLY inside the user's
// tap handler (no awaited network work between the gesture and create()),
// which preserves Safari's transient user activation and lets Face ID launch.

// Step 1 (network, run BEFORE the biometric tap): fetch challenge + options.
export async function getRegisterOptions() {
  const { data } = await api.post("/auth/passkey/register/options");
  return { pk: data.publicKey, tx: data.tx };
}

// Step 2 (must run inside the tap handler with NO await before it): launch the
// authenticator. Only synchronous base64→buffer conversion precedes create().
export async function createRegistrationCredential(pk) {
  const opts = {
    ...pk,
    challenge: b64uToBuf(pk.challenge),
    user: { ...pk.user, id: b64uToBuf(pk.user.id) },
    excludeCredentials: (pk.excludeCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
  };
  const cred = await navigator.credentials.create({ publicKey: opts });
  const r = cred.response;
  return {
    id: cred.id, rawId: bufToB64u(cred.rawId), type: cred.type,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      attestationObject: bufToB64u(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

// Step 3 (network, AFTER the biometric): verify + persist. Safe to await; may
// return 401 if step-up freshness is required — the same tx/credential can be
// retried after step-up because the challenge is single-use only on success.
export async function finishRegisterPasskey(tx, nickname, credential) {
  return (await api.post("/auth/passkey/register/finish", { tx, nickname, credential })).data;
}

// iOS-safe assertion: options are fetched up front (getLoginOptions /
// getStepUpOptions), then getAssertion() runs INSIDE the user's tap with only
// synchronous base64→buffer work before navigator.credentials.get(), so Safari
// keeps its transient user activation and Face ID / Touch ID can launch.
export async function getLoginOptions(email) {
  const { data } = await api.post("/auth/passkey/login/options", { email: email || null });
  return data; // { publicKey, tx }
}

export async function getStepUpOptions() {
  const { data } = await api.post("/auth/step-up/passkey/options");
  return data; // { publicKey, tx }
}

export async function getAssertion(pk) {
  const opts = {
    ...pk,
    challenge: b64uToBuf(pk.challenge),
    allowCredentials: (pk.allowCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
  };
  const cred = await navigator.credentials.get({ publicKey: opts });
  const r = cred.response;
  return {
    id: cred.id, rawId: bufToB64u(cred.rawId), type: cred.type,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      authenticatorData: bufToB64u(r.authenticatorData),
      signature: bufToB64u(r.signature),
      userHandle: r.userHandle ? bufToB64u(r.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

export async function finishLoginPasskey(tx, assertion) {
  return (await api.post("/auth/passkey/login/finish", { tx, credential: assertion })).data;
}

export async function finishStepUp(tx, assertion) {
  return (await api.post("/auth/step-up/passkey/verify", { tx, credential: assertion })).data;
}
